import React from 'react';

import Checkbox from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	checkboxMenuItem: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		minHeight: '32px',
		alignItems: 'flex-start',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		textAlign: 'left',
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.050'),
		paddingLeft: token('space.075'),
		paddingBottom: token('space.050'),
	},
	checkboxContent: {
		width: '100%',
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.075'),
		flex: '1',
		minWidth: '0px',
	},
	checkboxIcon: {
		display: 'flex',
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkboxLabelContainer: {
		flex: '1',
		minWidth: '0px',
	},
	lozengeContainer: {
		width: 'fit-content',
	},
});

interface CheckboxMenuItemProps {
	name: string;
	isChecked: boolean;
	label: string;
	lozengeText?: string;
	testId?: string;
	onChange: (enabled: boolean) => void;
	isStacked?: boolean;
}

export const CheckboxMenuItem = ({
	name,
	isChecked,
	label,
	lozengeText,
	testId = 'checkbox-menu-item-container',
	onChange,
	isStacked = true,
}: CheckboxMenuItemProps): React.JSX.Element => {
	const handleClick = () => {
		onChange(!isChecked);
	};

	return (
		<Pressable xcss={styles.checkboxMenuItem} onClick={handleClick} testId={testId}>
			<Box xcss={styles.checkboxContent}>
				<Box xcss={styles.checkboxIcon}>
					<Checkbox
						name={name}
						isChecked={isChecked}
						onChange={(e) => onChange(e.target.checked)}
						tabIndex={-1}
					/>
				</Box>
				<Box xcss={styles.checkboxLabelContainer}>
					<Flex
						direction={isStacked ? 'column' : 'row'}
						alignItems={isStacked ? 'start' : 'center'}
						columnGap="space.050"
						rowGap="space.050"
					>
						<Text color="color.text.subtle">{label}</Text>
						{lozengeText && (
							<Box xcss={styles.lozengeContainer}>
								<Lozenge appearance="neutral">{lozengeText}</Lozenge>
							</Box>
						)}
					</Flex>
				</Box>
			</Box>
		</Pressable>
	);
};
